import { Box, Grid, Typography } from '@mui/material';
import React from 'react';
import {
  Configuration,
  CONFIGURATION_TYPE,
  WORK_ORDER_STATUS,
  WorkOrder,
} from 'vendor-marketplace-shared-library';

import { renderTimeStatusOnWorkOrder, workOrderDueDate } from '../../utilities/formatters';
import { getConfigurationValue, isDateWithin24Hours } from '../../utilities/helpers';
import WorkOrderStatusChip from './WorkOrderStatusChip';
import useConfigurations from '../../hooks/useConfigurations';
import dayjs from 'dayjs';

type WorkOrderCardProps = {
  workOrder: WorkOrder;
  onClick: () => void;
};

export default function WorkOrderCard(props: WorkOrderCardProps) {
  const isWorkOrderComplete =
    Number(props.workOrder.workOrderStatus!.id) === WORK_ORDER_STATUS.DONE.id;
  const { configurationsQuery } = useConfigurations();

  const isPastDue = (dueDate: Date) => {
    return workOrderDueDate(dueDate) === 'Past due';
  };

  const generateDueDateTextStyles = (workOrder: WorkOrder) => {
    return isPastDue(workOrder.dueDate) &&
      !isDateWithin24Hours(workOrder.createdAt) &&
      !isWorkOrderComplete
      ? { color: 'error.main', fontWeight: '600' }
      : { color: 'secondary.main' };
  };

  const userConciergeFeatureFrontendEnabledConfiguration =
    configurationsQuery?.data?.getConfigurations?.find(
      (configuration: Configuration) =>
        configuration.name === CONFIGURATION_TYPE.CONCIERGE_FEATURE_FRONTEND_IS_ENABLED,
    );

  const isUserConciergeFeatureFrontendEnabled = getConfigurationValue(
    userConciergeFeatureFrontendEnabledConfiguration,
  );

  const userNonWarrantyWorkOrderCreationIsEnabledConfiguration =
    configurationsQuery?.data?.getConfigurations?.find(
      (configuration: Configuration) =>
        configuration.name === CONFIGURATION_TYPE.NON_WARRANTY_WORK_ORDER_CREATION_IS_ENABLED,
    );

  const isUserNonWarrantyWorkOrderCreationIsEnabled = getConfigurationValue(
    userNonWarrantyWorkOrderCreationIsEnabledConfiguration,
  );

  return (
    <Box sx={Styles.box} onClick={props.onClick}>
      <Grid container direction={'column'}>
        {/*
        Top Row
         */}
        <Grid item>
          <Grid
            container
            justifyContent={'space-between'}
            sx={{ maxWidth: { xs: '250px', sm: '365px' } }}
          >
            <Grid item sx={{ maxWidth: '64%' }}>
              <Typography noWrap>{props.workOrder.title || 'Untitled Work Order'}</Typography>
            </Grid>
            <Grid item>
              <WorkOrderStatusChip workOrderStatus={props.workOrder.workOrderStatus!} />
            </Grid>
          </Grid>
        </Grid>

        {/*
         Middle Row
         */}
        <Grid item>
          <Grid container direction={'row'} columnGap={0.5} alignItems={'center'}>
            <img src={'/icons/vendor_hammer_icon.svg'} alt={'Issue Type Icon'} />
            <Typography variant={'caption'}>{props.workOrder.issueType?.name}</Typography>
            <Typography>|</Typography>
            <img src={'/icons/property_icon.svg'} alt={'Property Icon'} />
            <Typography variant={'caption'}>{props.workOrder.property?.name}</Typography>
          </Grid>
        </Grid>

        {/*
        Bottom Row
          */}
        {isUserNonWarrantyWorkOrderCreationIsEnabled && (
          <Grid item>
            <Grid
              container
              direction={'row'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <Grid item>
                <Grid container alignItems={'center'} columnGap={1}>
                  <Grid item>
                    <img
                      src={`/icons/${props.workOrder.workOrderPriority!.name.toLowerCase()}_priority_icon.svg`}
                      alt={'Priority Icon'}
                    />
                  </Grid>
                  <Grid item>
                    <Typography variant={'caption'}>
                      {props.workOrder.workOrderPriority?.name}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {props.workOrder.conciergeEnabled && isUserConciergeFeatureFrontendEnabled && (
                <Grid item>
                  <Grid container alignItems={'center'} columnGap={0.5}>
                    <Grid item>
                      <img
                        src={'/procure_favicon.svg'}
                        alt={'Procure Icon'}
                        height={16}
                        width={16}
                      />
                    </Grid>
                    <Grid item>
                      <Typography variant={'caption'}>Bids Requested</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              <Grid item>
                <Typography variant={'caption'} sx={generateDueDateTextStyles(props.workOrder)}>
                  {renderTimeStatusOnWorkOrder(
                    isWorkOrderComplete,
                    props.workOrder.completedAt,
                    props.workOrder.createdAt,
                    props.workOrder.dueDate,
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid item container xs={12} justifyContent={'space-between'}>
          <Grid item>
            <Typography variant={'caption'} sx={{ color: 'secondary.main' }}>
              {`Created on ${dayjs(new Date(props.workOrder.createdAt)).format('MMM D, YYYY')}`}
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              variant={'caption'}
              sx={{ color: 'secondary.main' }}
            >{`Created by ${props.workOrder.createdByUser?.fullName}`}</Typography>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

const Styles = {
  box: {
    borderRadius: 1,
    border: '1px gray solid',
    width: { xs: '270px', sm: '365px' },
    padding: 1,
    backgroundColor: 'common.white',
    cursor: 'pointer',
  },
};
